import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  alpha,
  Tooltip,
  Stack,
  Divider,
} from "@mui/material";
import { groupByYA } from "../utils/dateUtils";
import { processYARounding } from "../utils/rounding";
import { formatAmount } from "../../../../../../utils/hooks/dataHandler";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../../../utils/export/componentExporter";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import CancelIcon from "@mui/icons-material/Cancel";
import ReconciliationDisplay from "./ReconciliationDisplay";

const totalRowStyle = {
  fontWeight: 700,
  color: "text.primary",
  py: 2,
  borderTop: "2px solid",
  borderColor: (theme) => alpha(theme.palette.divider, 0.3),
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
};

const PreUploadTableView = ({
  schedule,
  step2ComputedResults,
  step2Inputs,
  assetCost,
  computedResults,
  onAddToHPSchedule,
  onCancel,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const exportRef = useRef(null);

  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(
        exportRef.current,
        "Capital Allowance Upload Table"
      );
    }
  };

  const { results, adjustment } = useMemo(() => {
    if (!schedule || !step2Inputs) {
      return { results: null, adjustment: null };
    }

    // Use computed results if provided, otherwise compute here
    let roundedGroups, adj;
    if (computedResults) {
      roundedGroups = computedResults.roundedGroups;
      adj = computedResults.adjustment;
    } else {
      const inAdvance =
        schedule.inAdvance !== undefined ? schedule.inAdvance : false;
      const yaGroups = groupByYA(schedule, step2Inputs.fyEndMonth, inAdvance);

      const processed = processYARounding(yaGroups, assetCost);
      roundedGroups = processed.roundedGroups;
      adj = processed.adjustment;
    }

    // Format results for table
    const numberOfYears = step2Inputs.numberOfYears;

    const tableRows = roundedGroups.map((group) => ({
      assetCode: step2Inputs.assetCode,
      assetName: step2Inputs.assetName,
      cost: assetCost,
      caType: step2Inputs.caType,
      numberOfYears: numberOfYears,
      initialAllowanceRate: step2Inputs.initialAllowanceRate,
      ya: group.ya,
      principalPaid: group.principalPaid,
    }));

    return {
      results: tableRows,
      adjustment: adj,
    };
  }, [schedule, step2Inputs, assetCost, computedResults]);

  if (!results || results.length === 0) {
    return null;
  }

  const copyTableToClipboard = async () => {
    try {
      const headers = [
        "Asset Name",
        "Cost",
        "CA Type",
        "No. of Years of Claims",
        "YA of Principal Paid",
        "Principal Paid",
      ];
      const rows = results.map((row) => [
        row.assetName,
        formatAmount(row.cost),
        row.caType,
        row.numberOfYears !== "" &&
        row.numberOfYears !== null &&
        row.numberOfYears !== undefined
          ? row.numberOfYears
          : "-",
        row.ya,
        formatAmount(row.principalPaid),
      ]);

      const csvContent = [
        headers.join("\t"),
        ...rows.map((row) => row.join("\t")),
      ].join("\n");

      await navigator.clipboard.writeText(csvContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy table:", err);
      alert("Failed to copy table. Please try again.");
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1] || "";
  };

  return (
    <Box ref={exportRef}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant='h6' fontWeight={600}>
            Capital Allowance Upload Table
          </Typography>
          {step2Inputs.fyEndMonth && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Financial Year-End:{" "}
              <strong>{getMonthName(step2Inputs.fyEndMonth)}</strong>
            </Typography>
          )}
        </Box>
        <Box
          className='no-export'
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the capital allowance upload table'
          />
          <Button
            onClick={copyTableToClipboard}
            variant='outlined'
            size='small'
            startIcon={<CopyAllIcon />}
          >
            {copySuccess ? "✓ Copied!" : "Copy Table"}
          </Button>
        </Box>
      </Box>

      {adjustment && adjustment.warning && (
        <Alert
          severity='warning'
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.warning.main, 0.2),
          }}
        >
          <Typography variant='subtitle2' gutterBottom>
            <strong>Warning:</strong> Total principal paid does not reconcile to
            asset cost.
          </Typography>
          <Typography variant='body2'>
            Difference: {formatAmount(adjustment.difference)}
          </Typography>
        </Alert>
      )}

      {/* Side by Side Layout */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        {/* Asset Information Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            flex: "0 0 300px",
            minWidth: 0,
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight={600}
            color='primary.main'
            gutterBottom
            sx={{ mb: 2 }}
          >
            Asset Information
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Asset Name
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {results[0]?.assetName || "-"}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Cost
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {formatAmount(assetCost)}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                CA Type
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {results[0]?.caType || "-"}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                No. of Years of Claims
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {results[0]?.numberOfYears !== "" &&
                results[0]?.numberOfYears !== null &&
                results[0]?.numberOfYears !== undefined
                  ? results[0]?.numberOfYears
                  : "-"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Principal Paid by YA Table */}
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.divider, 0.1),
              overflow: "auto",
              maxWidth: "100%",
              "& .MuiTable-root": {
                minWidth: "max-content",
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      borderBottom: "2px solid",
                      borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                      py: 2,
                    }}
                  >
                    YA of Principal Paid
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      borderBottom: "2px solid",
                      borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                      py: 2,
                    }}
                  >
                    Principal Paid
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        py: 1.5,
                      }}
                    >
                      {row.ya}
                    </TableCell>
                    <TableCell align='right' sx={{ py: 1.5 }}>
                      {formatAmount(row.principalPaid)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={totalRowStyle}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align='right' sx={totalRowStyle}>
                    {formatAmount(
                      results.reduce((sum, row) => sum + row.principalPaid, 0)
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {step2ComputedResults &&
        step2ComputedResults.adjustment &&
        step2ComputedResults.adjustment.adjusted && (
          <ReconciliationDisplay
            adjustment={step2ComputedResults.adjustment}
            originalTotal={step2ComputedResults.originalTotal}
            adjustedTotal={step2ComputedResults.adjustedTotal}
          />
        )}

      {onAddToHPSchedule && (
        <Box
          className='no-export'
          sx={{ mt: 3, display: "flex", justifyContent: "center" }}
        >
          <Tooltip title='Cancel the upload and return to the previous step'>
            <Button
              onClick={onCancel}
              variant='outlined'
              size='small'
              startIcon={<CancelIcon />}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
          </Tooltip>
          <Button
            onClick={() => onAddToHPSchedule(results)}
            variant='contained'
            color='primary'
            size='large'
            sx={{ minWidth: 200 }}
          >
            Add to HP Schedule
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PreUploadTableView;
